import * as React from 'react'
import { FixedSizeList as List } from 'react-window'
import type { ListOnItemsRenderedProps } from 'react-window'
import { m, useMotionValue, animate, LazyMotion, domAnimation } from 'framer-motion'
import { useMatchMedia } from 'hooks/use-match-media'
import { tw } from 'utils/classnames'

import { CarouselItem } from './carousel-item'
import type { CarouselItemData } from './carousel-item'
import { DEFAULT_SHADOW_COLOR, EdgeButton, EdgeScrim } from './overflow-edge'
import type { ButtonSize, ButtonVariant } from '@repo/studio-design-system-ui-button'

/**
 * Number of times to repeat the item list when looping is active.
 * This creates a virtual list large enough that users will never reach the edge.
 */
const LOOP_REPEAT_FACTOR = 100
/** Fraction of the carousel at each end whose hover reveals that end's scroll button. */
const HOVER_ZONE_RATIO = 0.2
const LIST_STYLE = { scrollbarWidth: 'none' } as const

export type MediaCarouselDensity = 'default' | 'compact'

/** Item and track sizes from Figma. Compact tightens both for narrower containers. */
const densitySizes = {
  default: { itemWidth: 232, height: 348 },
  compact: { itemWidth: 200, height: 300 },
} satisfies Record<MediaCarouselDensity, { itemWidth: number; height: number }>

/** A fixed size, or one derived from the measured container width so items can size fluidly. */
export type CarouselSize = number | ((containerWidth: number) => number)

interface MediaCarouselProps<T> {
  items: T[]
  ItemComponent: React.ComponentType<{ item: T; realIndex: number }>
  /** Sizes items and track. Either size can be overridden individually. @default 'default' */
  density?: MediaCarouselDensity
  itemWidth?: CarouselSize
  gap?: number
  /** Wraps from the last item back to the first. A paginated list never loops. @default true */
  loop?: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  previousLabel?: string
  nextLabel?: string
  className?: string
  /**
   * The button height used to position the scroll buttons.
   * This may be different from the item height, in order to align the buttons with something
   * specific from the item.
   */
  buttonHeight?: CarouselSize
  height?: CarouselSize
  /**
   * Layered over the default scrim, so a carousel can match the surface it sits on. The default
   * suits a `gray-1000` surface; lighter surfaces want the `subtle` or `faint` tier of the same
   * token.
   */
  shadowColor?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  renderScrollButton?: (props: ScrollButtonRenderProps) => React.ReactNode
}

export interface ScrollButtonRenderProps {
  variant: 'left' | 'right'
  buttonHeight: number
  onClick: () => void
}

export interface ResponsiveMediaCarouselLayout {
  className: string
  itemWidth: number
  height: number
  buttonHeight?: number
  buttonSize?: ButtonSize
}

interface ResponsiveMediaCarouselProps<T> {
  items: T[]
  ItemComponent: React.ComponentType<{ item: T; realIndex: number }>
  layouts: readonly ResponsiveMediaCarouselLayout[]
  gap?: number
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  previousLabel?: string
  nextLabel?: string
  shadowColor?: string
}

/**
 * Renders one carousel per breakpoint layout, each shown by its own `className`.
 *
 * @deprecated Pass functions to `itemWidth` / `height` on a single `MediaCarousel` instead — they
 * receive the measured container width, so one instance can size itself fluidly.
 */
export function ResponsiveMediaCarousel<T>({
  items,
  ItemComponent,
  layouts,
  gap,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  previousLabel,
  nextLabel,
  shadowColor,
}: ResponsiveMediaCarouselProps<T>) {
  return (
    <>
      {layouts.map(layout => (
        <MediaCarousel
          key={layout.className}
          items={items}
          ItemComponent={ItemComponent}
          gap={gap}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          previousLabel={previousLabel}
          nextLabel={nextLabel}
          shadowColor={shadowColor}
          {...layout}
        />
      ))}
    </>
  )
}

/**
 * A horizontally scrolling carousel for collections of media content.
 *
 * Items are virtualized, so a long or paginated list only ever renders what is on screen. Items
 * beyond the visible area are reachable through the scroll buttons, which sit above a gradient
 * scrim on whichever edge can still be scrolled towards — revealed on hover, and always visible on
 * touch devices, where there is no hover.
 *
 * @figma https://www.figma.com/design/mKWuIdPpAnPyyQnGc1pbY5/Studio-DS---Components?node-id=1507-4620&m=dev
 */
export function MediaCarousel<T>({
  items,
  ItemComponent,
  density = 'default',
  itemWidth = densitySizes[density].itemWidth,
  gap = 8,
  loop = true,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  previousLabel = 'Scroll to previous',
  nextLabel = 'Scroll to next',
  className,
  height = densitySizes[density].height,
  buttonHeight = height,
  shadowColor,
  buttonVariant = 'secondary',
  buttonSize = 'sm',
  renderScrollButton,
}: MediaCarouselProps<T>) {
  const listRef = React.useRef<List>(null)
  const outerRef = React.useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)
  // Figma keeps the buttons visible wherever there is no hover state to reveal them.
  const hasHover = useMatchMedia('(hover: hover)')

  const lastScrollOffsetRef = React.useRef(0)
  const loopInitializedRef = React.useRef(false)
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null)

  const hoverSideRef = React.useRef<'left' | 'right' | null>(null)
  const canScrollLeftRef = React.useRef(false)
  const canScrollRightRef = React.useRef(false)

  // Framer motion values — updated imperatively, no re-renders.
  const leftShadowOpacity = useMotionValue(0)
  const rightShadowOpacity = useMotionValue(0)
  const leftButtonOpacity = useMotionValue(0)
  const rightButtonOpacity = useMotionValue(0)

  const scrimClassName = tw(DEFAULT_SHADOW_COLOR, shadowColor)
  const resolvedItemWidth = resolveSize(itemWidth, containerWidth)
  const resolvedHeight = resolveSize(height, containerWidth)
  const resolvedButtonHeight = resolveSize(buttonHeight, containerWidth)

  const isLooping = loop && !hasNextPage && items.length > 0
  const totalItemWidth = resolvedItemWidth + gap
  const virtualItemCount = isLooping ? items.length * LOOP_REPEAT_FACTOR : items.length

  if (!isLooping) {
    loopInitializedRef.current = false
  }

  const containerRefCallback = React.useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()

    if (node) {
      setContainerWidth(node.offsetWidth)
      const observer = new ResizeObserver(() => {
        setContainerWidth(node.offsetWidth)
      })
      observer.observe(node)
      resizeObserverRef.current = observer
    }
  }, [])

  // --- Stable callbacks for react-window ---
  // FixedSizeList extends PureComponent. New function references for onScroll,
  // onItemsRendered, or children cause a full List re-render (including the
  // inner container div), which causes a visual flicker.
  //
  // We keep the latest handler logic in a ref and create a stable wrapper
  // that delegates to it. The wrapper never changes identity.

  function handleItemsRendered({ visibleStopIndex }: ListOnItemsRenderedProps) {
    if (isLooping && !loopInitializedRef.current) {
      loopInitializedRef.current = true
      const middleIndex = Math.floor(virtualItemCount / 2)
      const alignedIndex = middleIndex - (middleIndex % items.length)
      listRef.current?.scrollToItem(alignedIndex, 'start')
    }

    if (hasNextPage && !isFetchingNextPage) {
      const visibleCount = Math.ceil(containerWidth / totalItemWidth)
      const remainingItems = items.length - visibleStopIndex
      if (remainingItems < visibleCount) {
        fetchNextPage()
      }
    }
  }

  function handleScroll(scrollData: { scrollOffset: number; scrollUpdateWasRequested: boolean }) {
    // react-window bug workaround: when items change it may reset scroll to 0.
    if (scrollData.scrollOffset === 0 && scrollData.scrollUpdateWasRequested) {
      listRef.current?.scrollTo(lastScrollOffsetRef.current)
      return
    }
    lastScrollOffsetRef.current = scrollData.scrollOffset
    const canScrollLeft = scrollData.scrollOffset > 0
    const canScrollRight = canScrollRightOf(outerRef.current, scrollData.scrollOffset)
    if (
      canScrollLeft !== canScrollLeftRef.current ||
      canScrollRight !== canScrollRightRef.current
    ) {
      canScrollLeftRef.current = canScrollLeft
      canScrollRightRef.current = canScrollRight
      updateOverlays()
    }
  }

  const handleScrollRef = React.useRef(handleScroll)
  handleScrollRef.current = handleScroll

  const handleItemsRenderedRef = React.useRef(handleItemsRendered)
  handleItemsRenderedRef.current = handleItemsRendered

  // Stable wrappers — created once, delegates to latest handler via ref.
  const stableOnScroll = React.useMemo(
    () => (data: { scrollOffset: number; scrollUpdateWasRequested: boolean }) =>
      handleScrollRef.current(data),
    [],
  )
  const stableOnItemsRendered = React.useMemo(
    () => (props: ListOnItemsRenderedProps) => handleItemsRenderedRef.current(props),
    [],
  )

  const itemData = React.useMemo<CarouselItemData>(
    () => ({
      items: items as unknown[],
      ItemComponent: ItemComponent as React.ComponentType<{ item: unknown; realIndex: number }>,
      isLooping,
      itemWidth: resolvedItemWidth,
      gap,
    }),
    [items, ItemComponent, isLooping, resolvedItemWidth, gap],
  )

  function scrollBy(direction: 'left' | 'right') {
    if (!outerRef.current) {
      return
    }
    // Scroll by whole items (including gap) that fill one viewport width
    const itemsPerPage = Math.max(1, Math.floor(containerWidth / totalItemWidth))
    const delta = itemsPerPage * totalItemWidth
    const offset = lastScrollOffsetRef.current
    const target = direction === 'left' ? Math.max(0, offset - delta) : offset + delta

    outerRef.current.scrollTo({ left: target, behavior: 'smooth' })
  }

  function updateOverlays() {
    const hover = hoverSideRef.current
    const canScrollLeft = canScrollLeftRef.current
    const canScrollRight = canScrollRightRef.current

    animate(leftShadowOpacity, canScrollLeft ? 1 : 0)
    animate(rightShadowOpacity, canScrollRight ? 1 : 0)
    animate(leftButtonOpacity, canScrollLeft && (!hasHover || hover === 'left') ? 1 : 0)
    animate(rightButtonOpacity, canScrollRight && (!hasHover || hover === 'right') ? 1 : 0)
  }

  const updateOverlaysRef = React.useRef(updateOverlays)
  updateOverlaysRef.current = updateOverlays

  // The list only reports offsets once it scrolls, so the resting state is measured here —
  // otherwise a carousel that overflows on load shows no scrim until the user scrolls it.
  React.useLayoutEffect(() => {
    const scroller = outerRef.current
    if (!scroller) {
      return
    }
    canScrollLeftRef.current = scroller.scrollLeft > 0
    canScrollRightRef.current = canScrollRightOf(scroller, scroller.scrollLeft)
    updateOverlaysRef.current()
  }, [containerWidth, items.length, resolvedItemWidth, resolvedHeight, hasHover])

  function scrollButton(position: 'left' | 'right') {
    const onClick = () => scrollBy(position)

    if (renderScrollButton) {
      return renderScrollButton({ variant: position, buttonHeight: resolvedButtonHeight, onClick })
    }

    return (
      <EdgeButton
        position={position}
        label={position === 'left' ? previousLabel : nextLabel}
        buttonHeight={resolvedButtonHeight}
        buttonVariant={buttonVariant}
        buttonSize={buttonSize}
        onClick={onClick}
      />
    )
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const side = hoveredSide(rect, e.clientX)
    if (side !== hoverSideRef.current) {
      hoverSideRef.current = side
      updateOverlays()
    }
  }

  function handleMouseLeave() {
    hoverSideRef.current = null
    updateOverlays()
  }

  return (
    <div
      ref={containerRefCallback}
      className={tw('relative isolate', className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {containerWidth > 0 ? (
        <List
          className="!overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={LIST_STYLE}
          height={resolvedHeight}
          itemCount={virtualItemCount}
          itemSize={totalItemWidth}
          width={containerWidth}
          layout="horizontal"
          ref={listRef}
          outerRef={outerRef}
          itemData={itemData}
          onItemsRendered={stableOnItemsRendered}
          onScroll={stableOnScroll}
        >
          {CarouselItem}
        </List>
      ) : null}

      <LazyMotion features={domAnimation}>
        <m.div style={{ opacity: leftShadowOpacity }}>
          <EdgeScrim position="left" className={scrimClassName} />
        </m.div>
        <m.div style={{ opacity: rightShadowOpacity }}>
          <EdgeScrim position="right" className={scrimClassName} />
        </m.div>

        <m.div style={{ opacity: leftButtonOpacity }}>{scrollButton('left')}</m.div>
        <m.div style={{ opacity: rightButtonOpacity }}>{scrollButton('right')}</m.div>
      </LazyMotion>
    </div>
  )
}

function resolveSize(size: CarouselSize, containerWidth: number) {
  return typeof size === 'function' ? size(containerWidth) : size
}

function canScrollRightOf(scroller: HTMLDivElement | null, scrollOffset: number) {
  return scroller ? scrollOffset < scroller.scrollWidth - scroller.clientWidth : false
}

function hoveredSide(rect: DOMRect, clientX: number): 'left' | 'right' | null {
  const offset = clientX - rect.left
  const zone = rect.width * HOVER_ZONE_RATIO

  if (offset <= zone) {
    return 'left'
  }

  if (offset >= rect.width - zone) {
    return 'right'
  }

  return null
}
